import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  CheckCircleIcon,
  DocumentCheckIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const GSTComplianceGuide: React.FC = () => {
  const monthlyChecklist = [
    'Collect all sales and purchase invoices from clients',
    'Verify GSTIN of all suppliers and customers',
    'Validate HSN/SAC codes for all transactions',
    'Reconcile invoices with previous month GSTR-2A/2B',
    'Calculate output GST liability',
    'Calculate eligible Input Tax Credit (ITC)',
    'Prepare and file GSTR-1 by 11th of next month',
    'Prepare and file GSTR-3B by 20th of next month',
    'Make GST payment before due date',
    'Download and verify GSTR-2B for ITC reconciliation',
  ];

  const commonMistakes = [
    {
      mistake: 'Incorrect GSTIN Entry',
      consequence: 'ITC claim rejection, penalty up to ₹25,000',
      solution: 'Use AI invoice scanner to auto-validate GSTIN format',
    },
    {
      mistake: 'Wrong HSN Code Selection',
      consequence: 'Tax rate mismatch, notice from GST department',
      solution: 'Automated HSN code validation against government master',
    },
    {
      mistake: 'Missing Invoices in Returns',
      consequence: 'ITC loss, mismatch in annual reconciliation',
      solution: 'Client portal ensures all invoices are captured',
    },
    {
      mistake: 'Late Filing of Returns',
      consequence: 'Late fee of ₹50/day (₹20/day for nil returns)',
      solution: 'Automated reminders and faster processing with AI',
    },
  ];

  return (
    <>
      <SEO
        title="GST Compliance Checklist 2025 - Complete Guide for CA Firms"
        description="Complete GST compliance checklist for 2025. Monthly GST filing deadlines, common mistakes to avoid, and automation tips for chartered accountants. Free downloadable checklist."
        keywords="gst compliance checklist, gst filing deadlines 2025, gst compliance guide, monthly gst checklist, gst return filing guide"
        canonicalUrl="https://aicafriend.com/gst-compliance-guide"
      />

      <div className="bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-amber-50 via-white to-orange-50 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
            <div className="text-center">
              <div className="inline-flex items-center bg-amber-100 text-amber-800 px-4 py-2 rounded-full mb-6">
                <DocumentCheckIcon className="h-5 w-5 mr-2" />
                <span className="font-semibold">Updated for 2025</span>
              </div>

              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                Complete GST Compliance
                <span className="gradient-text"> Guide & Checklist 2025</span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
                Month-by-month GST compliance checklist for CA firms. Filing deadlines, common mistakes,
                and how to automate compliance with AI. Free downloadable PDF checklist included.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="#"
                  className="btn-primary px-8 py-4 text-lg font-semibold rounded-lg text-white shadow-lg"
                >
                  Download Free Checklist PDF
                </a>
                <Link
                  to="/free-gst-software"
                  className="btn-secondary px-8 py-4 text-lg font-semibold rounded-lg"
                >
                  Automate Compliance
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Monthly Checklist Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Monthly GST Compliance Checklist
              </h2>
              <p className="text-xl text-gray-600">
                Follow this checklist every month to ensure 100% GST compliance
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-8">
              <div className="space-y-4">
                {monthlyChecklist.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-4 mt-0.5">
                      <span className="text-green-600 font-semibold text-sm">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-700 text-lg">{item}</p>
                    </div>
                    <CheckCircleIcon className="h-6 w-6 text-gray-300 ml-4 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Filing Deadlines Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                GST Filing Deadlines 2025
              </h2>
              <p className="text-xl text-gray-600">
                Mark these important dates in your calendar
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center mb-4">
                  <CalendarDaysIcon className="h-8 w-8 text-blue-600 mr-3" />
                  <h3 className="text-2xl font-bold text-gray-900">11th</h3>
                </div>
                <p className="text-gray-600 mb-2">GSTR-1 Filing</p>
                <p className="text-sm text-gray-500">Outward supply details of previous month</p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center mb-4">
                  <CalendarDaysIcon className="h-8 w-8 text-purple-600 mr-3" />
                  <h3 className="text-2xl font-bold text-gray-900">13th</h3>
                </div>
                <p className="text-gray-600 mb-2">GSTR-1 (Quarterly)</p>
                <p className="text-sm text-gray-500">For businesses with turnover up to ₹5 crore</p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center mb-4">
                  <CalendarDaysIcon className="h-8 w-8 text-red-600 mr-3" />
                  <h3 className="text-2xl font-bold text-gray-900">20th</h3>
                </div>
                <p className="text-gray-600 mb-2">GSTR-3B Filing</p>
                <p className="text-sm text-gray-500">Summary return and payment due date</p>
              </div>
            </div>

            <div className="mt-8 bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
              <div className="flex">
                <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Late Filing Penalties</h4>
                  <ul className="text-gray-700 text-sm space-y-1">
                    <li>• GSTR-1/3B: ₹50 per day (₹20/day for nil returns)</li>
                    <li>• Maximum penalty: ₹5,000 per return</li>
                    <li>• Interest on late payment: 18% per annum</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Common Mistakes Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Common GST Compliance Mistakes to Avoid
              </h2>
              <p className="text-xl text-gray-600">
                Learn from others' mistakes and protect your practice
              </p>
            </div>

            <div className="space-y-6">
              {commonMistakes.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-200"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div>
                      <h4 className="font-semibold text-red-600 mb-2">❌ Mistake</h4>
                      <p className="text-gray-900">{item.mistake}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-orange-600 mb-2">⚠️ Consequence</h4>
                      <p className="text-gray-700">{item.consequence}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-green-600 mb-2">✅ Solution</h4>
                      <p className="text-gray-700">{item.solution}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Automation Benefits Section */}
        <section className="py-20 bg-gradient-to-r from-amber-600 to-orange-600">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ensure 100% GST Compliance with Automation
            </h2>
            <p className="text-xl text-amber-100 mb-8">
              Eliminate manual errors, meet all deadlines, and never miss a compliance requirement with AI-powered GST automation
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left mb-8">
              <div className="bg-white/10 rounded-lg p-6">
                <CheckCircleIcon className="h-8 w-8 text-white mb-3" />
                <h3 className="text-white font-semibold mb-2">Auto-Validate Everything</h3>
                <p className="text-amber-100 text-sm">GSTIN, HSN codes, tax rates validated automatically before filing</p>
              </div>
              <div className="bg-white/10 rounded-lg p-6">
                <CheckCircleIcon className="h-8 w-8 text-white mb-3" />
                <h3 className="text-white font-semibold mb-2">Never Miss Deadlines</h3>
                <p className="text-amber-100 text-sm">Automated reminders and faster processing ensure on-time filing</p>
              </div>
              <div className="bg-white/10 rounded-lg p-6">
                <CheckCircleIcon className="h-8 w-8 text-white mb-3" />
                <h3 className="text-white font-semibold mb-2">Complete Audit Trail</h3>
                <p className="text-amber-100 text-sm">Full documentation of all GST transactions for easy audits</p>
              </div>
              <div className="bg-white/10 rounded-lg p-6">
                <CheckCircleIcon className="h-8 w-8 text-white mb-3" />
                <h3 className="text-white font-semibold mb-2">Real-Time Compliance Alerts</h3>
                <p className="text-amber-100 text-sm">Get notified of any compliance issues before they become problems</p>
              </div>
            </div>

            <Link
              to="/free-gst-software"
              className="inline-block bg-white text-amber-600 px-8 py-4 text-lg font-semibold rounded-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-200"
            >
              Try Automated GST Compliance Free
            </Link>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-gray-50 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">More GST Resources</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link
                to="/resources"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Free GST Templates</h4>
                <p className="text-gray-600 text-sm">Download invoice templates and checklists</p>
              </Link>
              <Link
                to="/ai-invoice-processing"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">AI Invoice Processing</h4>
                <p className="text-gray-600 text-sm">Automate invoice data extraction</p>
              </Link>
              <Link
                to="/gst-automation-for-ca"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Complete GST Automation</h4>
                <p className="text-gray-600 text-sm">End-to-end GST workflow automation</p>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default GSTComplianceGuide;
